import { useRef } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  CHART_BUNDLE,
  patientChartApi,
  type PatientChartBundle,
} from '@/services/api/patientChartApi';
import { patientKeys } from './usePatients';
import { medicalRecordsKeys } from './useMedicalRecords';
import { attachmentsKeys } from './useAttachments';
import { imagingOrderKeys } from './useImagingOrders';
import { billingKeys } from './useBilling';

/**
 * Opening a chart fired ten patient-scoped requests. This fires one and
 * hands the pieces to the queries that used to make them.
 *
 * The deliberate choice here is *seeding* rather than replacing. Every
 * component keeps the `useQuery` it already had, under the key it already
 * had — so a prescription written from the chart still invalidates
 * `['prescriptions']` and still refreshes, the rail and the safety block
 * still share one `['medical-history', id]` entry, and if this endpoint
 * is missing (an older backend, a failed deploy) every one of those
 * queries simply fetches for itself exactly as before. A rewrite that
 * pulled the data out of those hooks would have had to re-solve
 * invalidation for all ten, and would have had no fallback.
 *
 * `null` sections are NOT seeded. Writing `null` into a query cache would
 * tell the component "the server says there are none", when what the
 * server said is "you may not read this" — so those keys are left alone
 * and the component's own (permission-gated, 403-ing) fetch stands.
 */

/** Filters whose exact identity the seeded query keys depend on. */
export const chartImagingFilters = (patientId: string) =>
  ({ patientId, limit: String(CHART_BUNDLE.listLimit) }) as const;

export const chartInvoiceFilters = (patientId: string) =>
  ({ patientId, limit: CHART_BUNDLE.listLimit }) as const;

export const chartVitalsKey = (patientId: string) =>
  ['vitals-history', patientId, CHART_BUNDLE.vitalsDays] as const;

export const patientChartKeys = {
  bundle: (patientId: string) => ['patient-chart', patientId] as const,
};

function seed(
  qc: QueryClient,
  patientId: string,
  bundle: PatientChartBundle,
): void {
  // `usePatient` reads the `{ success, data }` envelope, not the bare row.
  if (bundle.patient) {
    qc.setQueryData(patientKeys.detail(patientId), {
      success: true,
      data: bundle.patient,
    });
  }

  // The visits query is an infinite query, so it needs page structure —
  // one page, and the pagination envelope `usePatientMedicalRecords`
  // reads `total` and `hasNextPage` from.
  if (bundle.visits) {
    const { data, total } = bundle.visits;
    qc.setQueryData(
      [...medicalRecordsKeys.lists(), 'byPatient', patientId],
      {
        pages: [
          {
            data,
            pagination: {
              page: 1,
              limit: CHART_BUNDLE.visitLimit,
              total,
              totalPages: Math.ceil(total / CHART_BUNDLE.visitLimit),
              hasNextPage: data.length < total,
              hasPreviousPage: false,
            },
          },
        ],
        pageParams: [1],
      },
    );
  }

  if (bundle.history) qc.setQueryData(['medical-history', patientId], bundle.history);
  if (bundle.vitals) qc.setQueryData(chartVitalsKey(patientId), bundle.vitals);
  if (bundle.prescriptions) {
    qc.setQueryData(['prescriptions', 'byPatient', patientId], bundle.prescriptions);
  }
  if (bundle.labOrders) {
    qc.setQueryData(['clinic-lab-orders', 'by-patient', patientId], bundle.labOrders);
  }
  if (bundle.imagingOrders) {
    // `useImagingOrders` normalises to `{ data, total, page, limit }`.
    qc.setQueryData(imagingOrderKeys.list(chartImagingFilters(patientId)), {
      data: bundle.imagingOrders,
      total: bundle.imagingOrders.length,
      page: 1,
      limit: CHART_BUNDLE.listLimit,
    });
  }
  if (bundle.referrals) {
    qc.setQueryData(['referrals', 'by-patient', patientId], bundle.referrals);
  }
  if (bundle.attachments) {
    qc.setQueryData(attachmentsKeys.list(patientId), bundle.attachments);
  }
  if (bundle.invoices) {
    qc.setQueryData(billingKeys.list(chartInvoiceFilters(patientId)), {
      data: bundle.invoices,
      pagination: { page: 1, limit: CHART_BUNDLE.listLimit, total: bundle.invoices.length },
    });
  }
}

export interface ChartBundleState {
  /** True until the bundle has either landed or failed. */
  isLoading: boolean;
  /** Sections the server could not build; those queries will fetch normally. */
  failed: string[];
}

export function usePatientChartBundle(
  patientId: string | undefined,
): ChartBundleState {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: patientChartKeys.bundle(patientId ?? ''),
    queryFn: () => patientChartApi.get(patientId as string),
    enabled: !!patientId,
    staleTime: 30_000,
    // One attempt. This is an optimisation, not the data path — if it is
    // unavailable the individual queries are already the fallback, and
    // retrying just delays the chart behind a request we don't need.
    retry: false,
  });

  // Seeded during render, immediately before the caller renders the
  // children that read these keys. An effect would run after they had
  // already mounted and fired their own requests, which is the entire
  // thing this exists to avoid. `setQueryData` is idempotent, so
  // StrictMode's double render is harmless; the ref keeps it to once per
  // payload regardless.
  const seededFor = useRef<PatientChartBundle | null>(null);
  if (query.data && seededFor.current !== query.data && patientId) {
    seededFor.current = query.data;
    seed(qc, patientId, query.data);
  }

  return {
    isLoading: query.isLoading,
    failed: query.data?.failed ?? [],
  };
}
